import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { 
  Eye, 
  Download, 
  Copy, 
  Clock, 
  FileText, 
  Code, 
  Image, 
  Search, 
  Target, 
  TrendingUp,
  X,
  Check,
  Star,
  Users,
  Calendar,
  ExternalLink,
  ArrowLeft,
  ChevronDown,
  FileCode,
  Type,
  Hash,
  Store,
  Globe,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { sanitizeArticleHtml, getArticleIsolationStyles, getArticleScopeClass } from '@/utils/sanitizeArticleHtml'
import { PLATFORM_IMAGES } from '@/constants/platformImages'
import { useAdmin } from '@/hooks/useAdmin'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"

interface FullScreenArticlePreviewProps {
  articleId: string
  onClose: () => void
  onBack?: () => void
}

interface GeneratedArticle {
  id: string
  title: string
  content: string
  html_content: string
  meta_title: string
  meta_description: string
  primary_keyword: string
  secondary_keywords: string[]
  word_count: number
  reading_time: number
  featured_image_url?: string
  supporting_images: any[]
  image_prompts: string[]
  generation_model: string
  image_generation_model: string
  created_at: string
  completed_at: string
  generation_status: string
  progress: number
  published_status?: string
  published_platform?: string
  published_url?: string
  published_at?: string
}

export function FullScreenArticlePreview({ articleId, onClose, onBack }: FullScreenArticlePreviewProps) {
  const [article, setArticle] = useState<GeneratedArticle | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishPlatform, setPublishPlatform] = useState<string | null>(null)
  const [platformConnections, setPlatformConnections] = useState<any>({})
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [showWebflowQueuedDialog, setShowWebflowQueuedDialog] = useState(false)
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null)
  const { isAdmin } = useAdmin()
  const [adminWebsiteSettings, setAdminWebsiteSettings] = useState<any>(null)

  useEffect(() => {
    loadArticle()
    loadPlatformConnections()
    if (isAdmin) {
      loadAdminWebsiteSettings()
    }
  }, [articleId, isAdmin])

  const loadPlatformConnections = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: settings } = await supabase
        .from('user_settings')
        .select('platform_connections')
        .eq('user_id', user.id)
        .single()

      if (settings?.platform_connections) {
        setPlatformConnections(settings.platform_connections)
      }
    } catch (error) {
      console.error('Error loading platform connections:', error)
    }
  }

  const loadAdminWebsiteSettings = async () => {
    // No need to load settings for platform blog - it's always available for admins
    setAdminWebsiteSettings({ configured: true })
  }

  const loadArticle = async () => {
    try {
      const { data, error } = await supabase
        .from('generated_articles')
        .select('*')
        .eq('id', articleId)
        .single()

      if (error) throw error
      setArticle(data)
    } catch (error) {
      console.error('Error loading article:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(type)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (error) {
      // Silent fail
    }
  }

  const copyAsPlainText = () => {
    if (!article) return
    const plainText = article.content || article.html_content?.replace(/<[^>]*>/g, '') || ''
    copyToClipboard(plainText, 'Plain Text')
  }

  const copyAsMarkdown = () => {
    if (!article) return
    // Simple HTML to Markdown conversion
    let markdown = article.html_content || ''
    markdown = markdown
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
      .replace(/<[^>]*>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
    
    copyToClipboard(markdown, 'Markdown')
  }

  const downloadArticle = (format: 'html' | 'text') => {
    if (!article) return

    let content = ''
    let filename = ''
    let mimeType = ''

    if (format === 'html') {
      content = article.html_content || ''
      filename = `${(article.title || 'untitled').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`
      mimeType = 'text/html'
    } else {
      content = article.content || ''
      filename = `${(article.title || 'untitled').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`
      mimeType = 'text/plain'
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const getContentScore = () => {
    if (!article) return 0
    
    let score = 0
    
    // Word count scoring (target: 2000 words)
    if (article.word_count >= 1500) score += 25
    else if (article.word_count >= 1000) score += 15
    else if (article.word_count >= 500) score += 10
    
    // SEO elements
    if (article.meta_title && article.meta_title.length > 0) score += 15
    if (article.meta_description && article.meta_description.length > 0) score += 15
    if (article.primary_keyword) score += 10
    if (article.secondary_keywords && article.secondary_keywords.length > 0) score += 10
    
    // Images
    if (article.supporting_images && article.supporting_images.length > 0) score += 15
    if (article.featured_image_url) score += 10
    
    return Math.min(score, 100)
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50'
    if (score >= 60) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  const getConnectedPlatforms = () => {
    const connected: string[] = []
    if (platformConnections.wordpress?.connected) connected.push('wordpress')
    if (platformConnections.shopify?.connected) connected.push('shopify')
    if (platformConnections.wix?.connected) connected.push('wix')
    if (platformConnections.webflow?.connected) connected.push('webflow')
    return connected
  }

  const handlePublish = async (platform: string) => {
    if (!article) return

    // For admin website, check if configured
    if (platform === 'admin-website') {
      if (!adminWebsiteSettings?.admin_website_domain || !adminWebsiteSettings?.admin_website_username) {
        toast.error('Please configure your admin website settings first')
        setShowAdminWebsiteDialog(true)
        return
      }
    }

    setIsPublishing(true)
    setPublishPlatform(platform)
    setShowPublishDialog(false)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        toast.error('You must be logged in to publish')
        setIsPublishing(false)
        setPublishPlatform(null)
        return
      }

      // Get website ID if available
      const { data: website } = await supabase
        .from('websites')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_primary', true)
        .single()

      const { data, error } = await supabase.functions.invoke('publish-article', {
        body: {
          articleId: article.id,
          platform,
          websiteId: website?.id
        }
      })

      if (error) throw error

      if (data.success) {
        // For Webflow, show special dialog explaining queued status
        if (data.queued && platform === 'webflow') {
          setPublishedUrl(data.publishedUrl)
          setShowWebflowQueuedDialog(true)
          // Update article status without reloading to prevent flashing
          if (article) {
            setArticle({
              ...article,
              published_status: 'publishing',
              published_url: data.publishedUrl,
              published_platform: platform
            })
          }
        } else {
          toast.success(`Article published successfully to ${platform === 'admin-website' ? 'your website' : platform}!`)
          // Smoothly update article status without full reload
          if (article) {
            setArticle({
              ...article,
              published_status: 'published',
              published_url: data.publishedUrl,
              published_at: new Date().toISOString(),
              published_platform: platform
            })
          }
          // Reload article in background after a delay to prevent flashing
          setTimeout(() => {
            loadArticle()
          }, 500)
        }
      } else if (data.requiresConnection) {
        toast.error(`${platform.charAt(0).toUpperCase() + platform.slice(1)} not connected. Please connect in Settings.`, {
          action: {
            label: 'Go to Settings',
            onClick: () => {
              window.location.href = '/dashboard?tab=settings'
            }
          }
        })
      } else {
        throw new Error(data.error || 'Failed to publish article')
      }
    } catch (error: any) {
      console.error('Error publishing article:', error)
      toast.error(error.message || 'Failed to publish article')
    } finally {
      setIsPublishing(false)
      setPublishPlatform(null)
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl h-64 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading article preview...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!article) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl h-64 flex items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground text-lg mb-4">Article not found</p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const contentScore = getContentScore()

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-7xl h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-background to-muted/20">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="gap-2 hover:bg-muted/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <Check className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Generated Article</h1>
              <p className="text-muted-foreground">Your article is ready for review</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Publish Button */}
            <DropdownMenu open={showPublishDialog} onOpenChange={setShowPublishDialog}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  disabled={isPublishing || article.published_status === 'publishing'}
                  className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  {isPublishing || article.published_status === 'publishing' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Publishing...
                    </>
                  ) : article.published_status === 'published' ? (
                    <>
                      <Check className="h-4 w-4" />
                      Published
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4" />
                      Publish
                    </>
                  )}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl border border-border shadow-lg bg-background min-w-[200px]">
                {getConnectedPlatforms().length > 0 || isAdmin ? (
                  <>
                    {getConnectedPlatforms().length > 0 && (
                      <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                        Publish to
                      </div>
                    )}
                      {platformConnections.wordpress?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('wordpress')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.wordpress} alt="WordPress" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">WordPress</div>
                            <div className="text-xs text-muted-foreground">{platformConnections.wordpress.siteUrl}</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.shopify?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('shopify')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.shopify} alt="Shopify" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Shopify</div>
                            <div className="text-xs text-muted-foreground">{platformConnections.shopify.domain || platformConnections.shopify.storeUrl}</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.wix?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('wix')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.wix} alt="Wix" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Wix</div>
                            <div className="text-xs text-muted-foreground">Site ID: {platformConnections.wix.siteId}</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.webflow?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('webflow')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.webflow} alt="Webflow" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Webflow</div>
                            <div className="text-xs text-muted-foreground">CMS Publishing</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.framer?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('framer')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.framer} alt="Framer" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Framer</div>
                            <div className="text-xs text-muted-foreground">CMS Publishing</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <>
                          {getConnectedPlatforms().length > 0 && <Separator className="my-1" />}
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                            Admin Only
                          </div>
                          <DropdownMenuItem 
                            onClick={() => handlePublish('admin-website')}
                            disabled={isPublishing}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                          >
                            <div className="p-1.5 rounded-lg bg-primary/10">
                              <Globe className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-foreground">Publish to Platform Blog</div>
                              <div className="text-xs text-muted-foreground">
                                seoro.so/blog (SEO optimized)
                              </div>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}
                      {getConnectedPlatforms().length > 0 && (
                        <>
                          <Separator className="my-1" />
                          <DropdownMenuItem 
                            onClick={() => {
                              setShowPublishDialog(false)
                              window.location.href = '/dashboard?tab=settings'
                            }}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                          >
                            <div className="p-1.5 rounded-lg bg-primary/10">
                              <ExternalLink className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium text-foreground">Manage Connections</div>
                              <div className="text-xs text-muted-foreground">Add or remove platforms</div>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}
                    </>
                  ) : !isAdmin ? (
                    <>
                      <div className="px-4 py-3 text-center">
                        <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <div className="font-medium text-foreground mb-1">No Platforms Connected</div>
                        <div className="text-xs text-muted-foreground mb-3">
                          Connect a platform in Settings to publish articles
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowPublishDialog(false)
                            window.location.href = '/dashboard?tab=settings'
                          }}
                          className="w-full bg-gradient-to-r from-primary via-primary to-primary/90"
                        >
                          Go to Settings
                        </Button>
                      </div>
                    </>
                  ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 rounded-xl border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  {copiedField ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copiedField ? 'Copied!' : 'Copy'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl border border-border shadow-lg bg-background">
                <DropdownMenuItem 
                  onClick={() => copyToClipboard(article.html_content || '', 'HTML')}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                >
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <FileCode className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Copy as HTML</div>
                    <div className="text-xs text-muted-foreground">Raw HTML code</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={copyAsPlainText}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                >
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <Type className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Copy as Plain Text</div>
                    <div className="text-xs text-muted-foreground">Clean text only</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={copyAsMarkdown}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                >
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <Hash className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Copy as Markdown</div>
                    <div className="text-xs text-muted-foreground">Markdown format</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadArticle('html')}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
            <Button variant="outline" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Main Content - Split Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Side - Article Preview */}
          <div className="flex-1 min-w-0 overflow-hidden bg-muted/30">
            <ScrollArea className="h-full">
              <div className="h-full flex items-start justify-center py-8 px-6">
                <div className="w-full" style={{ maxWidth: '1600px' }}>
                  {/* Article Header */}
                  <div className="text-center mb-12 p-8 bg-gradient-to-r from-background to-muted/20 rounded-2xl border border-border/20 shadow-sm">
                    <h1 className="text-4xl font-bold text-foreground mb-6 leading-tight">
                      {article.title || 'Untitled Article'}
                    </h1>
                    <p className="text-xl text-muted-foreground mb-8">
                      {article.meta_description || 'No description available'}
                    </p>
                    <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2 bg-background/50 px-3 py-2 rounded-full">
                        <Clock className="h-4 w-4" />
                        <span>{article.reading_time || 0} min read</span>
                      </div>
                      <div className="flex items-center gap-2 bg-background/50 px-3 py-2 rounded-full">
                        <FileText className="h-4 w-4" />
                        <span>{article.word_count ? article.word_count.toLocaleString() : 'N/A'} words</span>
                      </div>
                      <div className="flex items-center gap-2 bg-background/50 px-3 py-2 rounded-full">
                        <Calendar className="h-4 w-4" />
                        <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Featured Image */}
                  {article.featured_image_url && (
                    <div className="mb-12">
                      <div className="relative overflow-hidden rounded-2xl shadow-xl border border-border/20">
                        <img 
                          src={article.featured_image_url} 
                          alt={article.title}
                          className="w-full h-80 object-cover transition-transform duration-300 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                      </div>
                    </div>
                  )}

                  {/* Article Content Container with elegant border */}
                  <div className="bg-background border border-border/50 rounded-2xl shadow-md overflow-hidden mb-12">
                    <div className="px-20 py-16">
                      <div className="prose prose-lg prose-xl max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-a:text-primary hover:prose-a:text-primary/80 prose-blockquote:border-l-primary prose-blockquote:bg-muted/20 prose-blockquote:rounded-r-lg prose-blockquote:p-4 prose-img:rounded-xl prose-img:shadow-lg">
                        <div 
                          className={getArticleScopeClass()}
                          style={getArticleIsolationStyles()}
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeArticleHtml(article.html_content)
                          }} 
                        />
                      </div>
                    </div>
                  </div>

                  {/* Supporting Images */}
                  {article.supporting_images && article.supporting_images.length > 0 && (
                    <div className="mt-16 p-8 bg-gradient-to-r from-background to-muted/20 rounded-2xl border border-border/20 shadow-sm">
                      <h3 className="text-2xl font-bold mb-8 text-foreground flex items-center gap-3">
                        <Image className="h-6 w-6" />
                        Article Images
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {article.supporting_images.map((image, index) => (
                          <div key={index} className="space-y-4">
                            <div className="relative overflow-hidden rounded-xl shadow-lg border border-border/20">
                              <img 
                                src={image.url || image} 
                                alt={image.prompt || `Article image ${index + 1}`}
                                className="w-full h-64 object-cover transition-transform duration-300 hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                            </div>
                            {image.prompt && (
                              <p className="text-sm text-muted-foreground italic bg-background/50 p-3 rounded-lg">
                                {image.prompt}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Right Side - Article Details & Analytics */}
          <div className="w-80 border-l bg-gradient-to-br from-muted/30 to-muted/10 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-6 space-y-6">
                  {/* Content Score */}
                  <Card className="bg-background border-border shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Star className="h-5 w-5" />
                        Content Score
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className={`inline-flex items-center px-4 py-2 rounded-full text-2xl font-bold ${getScoreColor(contentScore)}`}>
                        {contentScore}/100
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Based on SEO optimization, content quality, and structure
                      </p>
                    </CardContent>
                  </Card>

                  {/* Article Stats */}
                  <Card className="bg-background border-border shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Article Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Word Count</span>
                        <span className="font-semibold text-foreground">
                          {article.word_count ? article.word_count.toLocaleString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Reading Time</span>
                        <span className="font-semibold text-foreground">{article.reading_time || 0} min</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Images</span>
                        <span className="font-semibold text-foreground">
                          {article.supporting_images ? article.supporting_images.length : 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Generation Model</span>
                        <span className="font-semibold text-xs text-foreground">
                          {article.generation_model || 'N/A'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* SEO Information */}
                  <Card className="bg-background border-border shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Search className="h-5 w-5" />
                        SEO Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-foreground">Primary Keyword</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-sm">
                            {article.primary_keyword || 'N/A'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(article.primary_keyword || '', 'Primary Keyword')}
                          >
                            {copiedField === 'Primary Keyword' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          </Button>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground">Secondary Keywords</label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {article.secondary_keywords && article.secondary_keywords.length > 0 ? (
                            article.secondary_keywords.map((keyword, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {keyword}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">No secondary keywords</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground">Meta Title</label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-muted-foreground flex-1">
                            {article.meta_title || 'N/A'}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(article.meta_title || '', 'Meta Title')}
                          >
                            {copiedField === 'Meta Title' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          </Button>
                        </div>
                        {article.meta_title && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {article.meta_title.length}/60 characters
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground">Meta Description</label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-muted-foreground flex-1">
                            {article.meta_description || 'N/A'}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(article.meta_description || '', 'Meta Description')}
                          >
                            {copiedField === 'Meta Description' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          </Button>
                        </div>
                        {article.meta_description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {article.meta_description.length}/160 characters
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Image Prompts */}
                  {article.image_prompts && article.image_prompts.length > 0 && (
                    <Card className="bg-background border-border shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Image className="h-5 w-5" />
                          Image Prompts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {article.image_prompts.map((prompt, index) => (
                          <div key={index} className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-muted-foreground">Prompt {index + 1}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(prompt, `Image Prompt ${index + 1}`)}
                              >
                                {copiedField === `Image Prompt ${index + 1}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                              </Button>
                            </div>
                            <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg text-xs">
                              {prompt}
                            </p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Publishing Status */}
                  {article.published_status && article.published_status !== 'draft' && (
                    <Card className="bg-background border-border shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ExternalLink className="h-5 w-5" />
                          Publishing Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Status</span>
                          <Badge variant={
                            article.published_status === 'published' ? 'default' : 
                            article.published_status === 'publishing' ? 'secondary' : 
                            'destructive'
                          }>
                            {article.published_status === 'published' ? 'Published' :
                             article.published_status === 'publishing' ? 'Publishing...' :
                             'Failed'}
                          </Badge>
                        </div>
                        {article.published_platform && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Platform</span>
                            <span className="font-semibold text-foreground capitalize">{article.published_platform}</span>
                          </div>
                        )}
                        {article.published_url && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Published URL</span>
                            <a 
                              href={article.published_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline font-medium truncate max-w-[150px]"
                            >
                              View Article
                            </a>
                          </div>
                        )}
                        {article.published_at && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Published</span>
                            <span className="text-sm text-foreground">
                              {new Date(article.published_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Generation Details */}
                  <Card className="bg-background border-border shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Code className="h-5 w-5" />
                        Generation Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Status</span>
                        <Badge variant={article.generation_status === 'completed' ? 'default' : 'secondary'}>
                          {article.generation_status || 'Unknown'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Progress</span>
                        <span className="font-semibold text-foreground">{article.progress || 0}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Created</span>
                        <span className="text-sm text-foreground">
                          {article.created_at ? new Date(article.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Completed</span>
                        <span className="text-sm text-foreground">
                          {article.completed_at ? new Date(article.completed_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </div>
        </div>
      </div>

      {/* Webflow Queued Dialog */}
      <Dialog open={showWebflowQueuedDialog} onOpenChange={setShowWebflowQueuedDialog}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <img src={PLATFORM_IMAGES.webflow} alt="Webflow" className="h-6 w-6" />
              Article Queued for Publishing
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed pt-2">
              Your article has been successfully added to your Webflow CMS, but it's currently <strong>queued to publish</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">To make your article live:</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Go to your Webflow Designer</li>
                <li>Open the CMS panel and find your collection</li>
                <li>Click on the article</li>
                <li>Click the status dropdown (top right)</li>
                <li>Select <strong>"Publish now"</strong></li>
              </ol>
            </div>
            
            {publishedUrl && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Article URL:</span>
                <a 
                  href={publishedUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium truncate flex-1"
                >
                  {publishedUrl}
                </a>
              </div>
            )}
            
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-900 dark:text-blue-200">
                <strong>Note:</strong> The article will be visible at this URL once you publish it in Webflow Designer.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => setShowWebflowQueuedDialog(false)}
              className="rounded-full w-full"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Website Configuration Dialog - Removed, not needed for platform blog */}
    </div>
  )
}
